import { Organization, OrganizationMembership } from "../users/UserStore";
import axios, { TrimFields } from "../general/HttpClient";
import { ClientJS } from "clientjs";
import { singletonHook } from "react-singleton-hook";
import { useState, useEffect } from "react";

export const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
const SECURE_CLAIM = "sca-secure-signin";

export class Account {
    organization: string;
    firstName: string;
    lastName: string;
    fullName: string;
    isDisabled: boolean;
    thirdPartyLogin: boolean;
    dateCreated: string;
    dateUpdated: string;
    startDate: string;
    endDate: string;
    sourceID: string;
    source: string;
    id: string;
    userName: string;
    email: string;
    emailConfirmed: boolean;
    phoneNumber: string;
    photo: string;
    phoneNumberConfirmed: boolean;
    twoFactorEnabled: boolean;
    lockoutEnd: string;
    lockoutEnabled: boolean;
    data: any;
    metadata: Array<Metadata>;
    organizationMembership: Array<OrganizationMembership>;
    claims: Array<Claim>;
    permissions: Array<IdentityPermission>;
    groups: Array<Group>;

    constructor(json) {
        this.organization = json.organization;
        this.firstName = json.firstName;
        this.lastName = json.lastName;
        this.fullName = json.fullName;
        this.isDisabled = json.isDisabled;
        this.thirdPartyLogin = json.thirdPartyLogin;
        this.dateCreated = json.dateCreated;
        this.dateUpdated = json.dateUpdated;
        this.startDate = json.startDate;
        this.endDate = json.endDate;
        this.sourceID = json.sourceID;
        this.source = json.source;
        this.id = json.id;
        this.userName = json.userName;
        this.email = json.email;
        this.emailConfirmed = json.emailConfirmed;
        this.phoneNumber = json.phoneNumber;
        this.photo = json.photo;
        this.phoneNumberConfirmed = json.phoneNumberConfirmed;
        this.twoFactorEnabled = json.twoFactorEnabled;
        this.lockoutEnd = json.lockoutEnd;
        this.lockoutEnabled = json.lockoutEnabled;
        this.data = json.data;
        this.metadata = json.metadata;
        this.organizationMembership = json.organizationMembership;
        this.claims = json.claims;
        this.permissions = json.permissions;
        this.groups = json.groups;
    }

    isAdmin() {
        return this.claims && this.claims.filter((c) => c.type == ROLE_CLAIM && c.value == "Admin").length > 0;
    }

    isSuperAdmin() {
        return this.claims && this.claims.filter((c) => c.type == ROLE_CLAIM && c.value == "SuperAdmin").length > 0;
    }

    secureTimestamp() {
        let claim = this.claims && this.claims.filter((c) => c.type == SECURE_CLAIM);
        if (claim && claim.length > 0) {
            return claim[0].value;
        }
        return "";
    }

    hasPermission(permission) {
        return this.isAdmin() || (this.permissions && this.permissions.filter((p) => p.permission == permission).length > 0);
    }

    isStaff() {
        return this.organizationMembership && this.organizationMembership.filter((m) => m.type == 0 && m.enabled).length > 0;
    }
}

export class Metadata {
    id: string;
    key: string;
    value: string;
    overrideValue: string;
    enableOverride: boolean;
}

export class Claim {
    type: string;
    subject: any;
    properties: any;
    originalIssuer: string;
    issuer: string;
    valueType: string;
    value: string;
}

export class IdentityPermission {
    id: string;
    permission: string;
    userID: string;
    organizationID: string;
}

export class Group {
    id: string;
    name: string;
    type: number;
    organizationID: string;
    organization: Organization;
    source: string;
    sourceID: string;
    groupCustomTypeID: string;
    groupCustomType: GroupCustomType;
    data: any;
}

export class GroupCustomType {
    id: string;
    name: string;
    organizationID: string;
    source: string;
    sourceID: string;
}

let currentLoggedInUser = new Account({});

export function GetCurrentLoggedInUser(fetch?) {
    if (!fetch && currentLoggedInUser.id) {
        return Promise.resolve(currentLoggedInUser);
    } else {
        return axios.get("/account/currentloggedinuser").then((response) => {
            currentLoggedInUser = new Account(response.data);
            localStorage.setItem("user", JSON.stringify(response.data));
            return currentLoggedInUser;
        });
    }
}

const userFactory = () => {
    const [user, setUser] = useState(currentLoggedInUser);

    useEffect(() => {
        GetCurrentLoggedInUser().then((u) => {
            setUser(u);
        });
    }, []);

    return user;
};

export const useUser = singletonHook(currentLoggedInUser, userFactory);

export function ProfileRequirementsMet() {
    if (currentLoggedInUser != null)
        return (
            (!!currentLoggedInUser.fullName || (!!currentLoggedInUser.firstName && !!currentLoggedInUser.lastName)) &&
            !!currentLoggedInUser.phoneNumber &&
            !!currentLoggedInUser.email &&
            !!currentLoggedInUser.emailConfirmed
        );

    return false;
}

export function UpdateUser({ firstName, lastName, phoneNumber, email, id, twoFactorEnabled }) {
    return axios.post("/api/accountActions/updateUser", TrimFields({ firstName, lastName, phoneNumber, email, id, twoFactorEnabled })).then((response) => {
        return response.data;
    });
}

export function Fingerprint() {
    return new ClientJS().getFingerprint();
}

export const CoerceToArrayBuffer = (input) => {
    if (typeof input === "string") {
        // base64url to base64
        input = input.replace(/-/g, "+").replace(/_/g, "/");

        // base64 to Uint8Array
        var str = window.atob(input);
        var bytes = new Uint8Array(str.length);
        for (var i = 0; i < str.length; i++) {
            bytes[i] = str.charCodeAt(i);
        }
        input = bytes;
    }

    // Array to Uint8Array
    if (Array.isArray(input)) {
        input = new Uint8Array(input);
    }

    // Uint8Array to ArrayBuffer
    if (input instanceof Uint8Array) {
        input = input.buffer;
    }

    // error if none of the above worked
    if (!(input instanceof ArrayBuffer)) {
        throw new TypeError("could not coerce to ArrayBuffer");
    }

    return input;
};

export const CoerceToBase64Url = (input) => {
    // Array or ArrayBuffer to Uint8Array
    if (Array.isArray(input)) {
        input = Uint8Array.from(input);
    }

    if (input instanceof ArrayBuffer) {
        input = new Uint8Array(input);
    }

    // Uint8Array to base64
    if (input instanceof Uint8Array) {
        var str = "";
        var len = input.byteLength;

        for (var i = 0; i < len; i++) {
            str += String.fromCharCode(input[i]);
        }
        input = window.btoa(str);
    }

    if (typeof input !== "string") {
        throw new Error("could not coerce to string");
    }

    // base64 to base64url
    // NOTE: "=" at the end of challenge is optional, strip it off here
    input = input.replace(/\+/g, "-").replace(/\//g, "_").replace(/=*$/g, "");

    return input;
};

export const RegisterCredentialWithServer = (data, key) => {
    return axios.post("/Account/MakeCredentials", data, { params: { key } }).then((response) => response.data);
};

export const FetchMakeCredentialOptions = (deviceFingerprint) => {
    return axios.get(`/Account/CredentialOptions?device=${deviceFingerprint}`).then((response) => response.data);
};
export const GetCredentials = () => {
    return axios.get(`/api/AccountActions/Credentials`).then((response) => response.data);
};
export const DeleteCredential = (id) => {
    return axios.delete(`/api/AccountActions/Credentials/${id}`).then((response) => response.data);
};

// Web Push
export const GetVapidPublicKey = (): Promise<string> => {
    return axios.get(`/api/webpush/vapidPublicKey`).then((r) => r.data.publicKey);
};

export const SubscribeWebPush = (subscription: PushSubscription, key: string = "default") => {
    const sub = subscription.toJSON();
    return axios.post(`/api/webpush/subscribe`, {
        userID: null,
        key,
        pushSubscription: {
            endpoint: sub.endpoint,
            expirationTime: sub.expirationTime ?? null,
            keys: { p256dh: (sub.keys as any).p256dh, auth: (sub.keys as any).auth }
        }
    }).then((r) => r.data);
};

export const UnsubscribeWebPush = (endpoint: string) => {
    return axios.post(`/api/webpush/unsubscribe`, { endpoint }).then((r) => r.data);
};

export const GetWebPushSubscriptions = () => {
    return axios.get(`/api/webpush/subscriptions`).then((r) => r.data);
};

export const TestWebPush = () => {
    return axios.post(`/api/webpush/test`).then((r) => r.data);
};

// Login Approval
export const RequestLoginApproval = (username: string, returnUrl: string, context: string) => {
    return axios.post(`/api/loginApproval/request`, { username, returnUrl, context }).then((r) => r.data);
};

export const PollLoginApprovalStatus = (requestId: string): Promise<{ status: string }> => {
    return axios.get(`/api/loginApproval/${requestId}/status`).then((r) => r.data);
};

export const GetLoginApprovalRequest = (requestId: string) => {
    return axios.get(`/api/loginApproval/${requestId}`).then((r) => r.data);
};

export const ApproveLoginRequest = (requestId: string, assertion?: string) => {
    return axios.post(`/api/loginApproval/${requestId}/approve`, { assertion: assertion ?? null }).then((r) => r.data);
};

export const DenyLoginRequest = (requestId: string) => {
    return axios.post(`/api/loginApproval/${requestId}/deny`).then((r) => r.data);
};

export const GetApprovalAssertionOptions = (requestId: string) => {
    return axios.get(`/api/loginApproval/${requestId}/assertionOptions`).then((r) => r.data);
};

// OTP test harness (admin only)
export const TestRequestOtp = (subjectId: string, phoneNumber: string, purpose: string = "Activation") => {
    return axios
        .post(`/api/Users/testOtp/request`, {
            subjectId,
            phoneNumber,
            purpose,
            channel: "SMS",
        })
        .then((r) => r.data);
};

export const TestVerifyOtp = (challengeId: string, code: string, purpose: string = "Activation") => {
    return axios
        .post(`/api/Users/testOtp/verify`, {
            challengeId,
            code,
            purpose,
        })
        .then((r) => r.data);
};
